import React from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface JourneyStepItem {
  step: string;
  label: string;
  icon: string;
  count: number;
  pct: string;
  clip: string;
  padL: string;
  padR: string;
  delta: string;
  dColor: string;
  title: string;
  onClick: () => void;
}

export interface StatusSegmentItem {
  label: string;
  count: number;
  pct: string;
  color: string;
  onClick: () => void;
}

export interface JourneyNetworkSectionProps {
  row2Cols: string;
  journeyBase: number;
  statusCenter?: { value: number; unit: string };
  openAllCM: () => void;
  jGrid: string;
  jGap: string;
  jMin: string;
  jRadius: string;
  journey: JourneyStepItem[];
  donutBg: string;
  statusList: StatusSegmentItem[];
  showQuote: boolean;
}

export const JourneyNetworkSection: React.FC<JourneyNetworkSectionProps> = ({
  row2Cols,
  journeyBase,
  statusCenter,
  openAllCM,
  jGrid,
  jGap,
  jMin,
  jRadius,
  journey,
  donutBg,
  statusList,
  showQuote
}) => {
  const { t } = useTranslation();

  return (
    <div style={s(`display:grid;grid-template-columns:${row2Cols};gap:12px`)}>
      <section
        style={s(
          `background:#FFFEFB;border:1px solid #EFE6D6;border-radius:12px;box-shadow:0 1px 3px rgba(120,90,30,0.06);padding:16px 18px;min-width:0;display:flex;flex-direction:column;gap:14px`
        )}
      >
        <div style={s(`display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:space-between`)}>
          <h2
            style={s(
              `margin:0;font-size:14px;font-weight:700;color:#1B1B1B;text-transform:uppercase;letter-spacing:0.01em`
            )}
          >
            {t('managerNetwork.journey.title', 'Capital Manager Journey')}
          </h2>
          <span
            onClick={openAllCM}
            style={s(`font-size:11.5px;color:#6B6358;cursor:pointer;display:flex;align-items:center;gap:4px`)}
          >
            {t('managerNetwork.journey.fromTotal', 'Từ tổng số {{count}} Capital Managers', { count: journeyBase })}
            <i className="icon-arrow-right" style={s(`color:#B8893A`)}></i>
          </span>
        </div>
        <div style={s(`overflow-x:auto;padding-bottom:2px`)}>
          <div style={s(`display:grid;grid-template-columns:${jGrid};gap:${jGap};min-width:${jMin}`)}>
            {journey?.map((j, j_idx) => (
              <React.Fragment key={j_idx}>
                <div
                  onClick={j.onClick}
                  title={j.title}
                  style={s(
                    `position:relative;cursor:pointer;background:linear-gradient(90deg,#F5EAD6,#FBF5EA);clip-path:${j.clip};border-radius:${jRadius};padding:14px ${j.padR} 12px ${j.padL};display:flex;flex-direction:column;align-items:center;gap:3px;text-align:center;min-height:118px`
                  )}
                >
                  <i className={`icon-${j.icon}`} style={s(`font-size:21px;color:#B8893A;margin-bottom:2px`)}></i>
                  <span style={s(`font-size:19px;font-weight:700;color:#1B1B1B`)}>{j.count}</span>
                  <span style={s(`font-size:12px;color:#1B1B1B`)}>{j.label}</span>
                  <span style={s(`font-size:11.5px;color:#8A8278`)}>{j.pct}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section
        style={s(
          `background:#FFFEFB;border:1px solid #EFE6D6;border-radius:12px;box-shadow:0 1px 3px rgba(120,90,30,0.06);padding:16px 18px;min-width:0;display:flex;flex-direction:column;gap:12px`
        )}
      >
        <h2
          style={s(
            `margin:0;font-size:14px;font-weight:700;color:#1B1B1B;text-transform:uppercase;letter-spacing:0.01em;display:flex;align-items:center;gap:6px`
          )}
        >
          <span style={s(`color:#B8893A`)}>➝</span>{t('managerNetwork.journey.networkStatus', 'Network Status')}
        </h2>
        <div style={s(`display:flex;flex-wrap:wrap;align-items:center;gap:18px`)}>
          <div
            style={s(
              `width:124px;height:124px;border-radius:50%;background:${donutBg};display:flex;align-items:center;justify-content:center;flex:none`
            )}
          >
            <div
              style={s(
                `width:84px;height:84px;border-radius:50%;background:#FFFEFB;display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1.1`
              )}
            >
              <span style={s(`font-size:22px;font-weight:700;color:#1B1B1B`)}>{statusCenter ? statusCenter.value : journeyBase}</span>
              <span style={s(`font-size:11px;color:#3A342C`)}>{statusCenter ? statusCenter.unit : 'CM'}</span>
            </div>
          </div>
          <div style={s(`flex:1;min-width:170px;display:flex;flex-direction:column`)}>
            {statusList?.map((st, st_idx) => (
              <React.Fragment key={st_idx}>
                <div
                  style={s(
                    `display:grid;grid-template-columns:12px 1fr 30px 38px;align-items:center;gap:8px;padding:4px 6px;border-radius:6px;font-size:12px`
                  )}
                >
                  <span style={s(`width:10px;height:10px;border-radius:50%;background:${st.color}`)}></span>
                  <span style={s(`color:#1B1B1B`)}>{st.label}</span>
                  <span style={s(`font-weight:700;color:#1B1B1B;text-align:right`)}>{st.count}</span>
                  <span style={s(`color:#3A342C;text-align:right`)}>{st.pct}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {Boolean(showQuote) && (
        <div
          style={s(
            `background:linear-gradient(160deg,#F8EEDB,#FBF6EC);border:1px solid #EFE6D6;border-radius:12px;padding:18px 16px;display:flex;flex-direction:column;justify-content:center;gap:10px`
          )}
        >
          <span style={s(`font-family:'Cormorant Garamond',Georgia,serif;font-size:34px;line-height:0.6;color:#8C6424`)}>
            “
          </span>
          <span
            style={s(
              `font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;font-size:17px;line-height:1.35;color:#5A4A30;white-space:pre-line`
            )}
          >
            {t('managerNetwork.journey.quote', 'Con người đúng\nHệ thống đúng\nGiá trị thật')}
          </span>
          <span style={s(`font-size:10px;letter-spacing:0.12em;color:#8A8278`)}>BEQ OPS LUMIR</span>
        </div>
      )}
    </div>
  );
};
